import { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';

import { Popover } from '@/mastodon/components/popover';
import { Icon } from 'mastodon/components/icon';
import SaveIcon from '@/material-icons/400-24px/save.svg?react';
import DeleteIcon from '@/material-icons/400-24px/delete.svg?react';

export const DraftsDropdown: React.FC<{ 
  currentText: string; 
  initialDraft: string; 
  onLoad: (text: string) => void; 
}> = ({ currentText, initialDraft, onLoad }) => {
  const [open, setOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [drafts, setDrafts] = useState<{id: number, text: string, date: string}[]>([]);

  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem('mastodon_custom_drafts');
      if (saved) {
        try {
          setDrafts(JSON.parse(saved));
        } catch (e) {
          setDrafts([]);
        }
      }
    }
  }, [open]);

  const saveDraftsToStorage = (newDrafts: any[]) => {
    localStorage.setItem('mastodon_custom_drafts', JSON.stringify(newDrafts));
    setDrafts(newDrafts);
  };

  const handleSaveCurrent = () => {
    if (!currentText || currentText.trim() === '') {
      alert('저장할 내용이 없습니다.');
      return;
    }
    const newDraft = {
      id: Date.now(),
      text: currentText,
      date: new Date().toLocaleString()
    };
    saveDraftsToStorage([newDraft, ...drafts]);
    alert('현재 글이 임시저장 되었습니다.');
  };

  const handleDelete = (e: React.MouseEvent, id: number) => {
    e.stopPropagation(); 
    if (window.confirm('이 임시저장 글을 삭제하시겠습니까?')) {
      const newDrafts = drafts.filter(d => d.id !== id);
      saveDraftsToStorage(newDrafts);
    }
  };

  const handleToggle = useCallback(() => setOpen(!open), [open]);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type='button'
        ref={setTargetElement}
        title="임시저장 목록"
        aria-expanded={open}
        onClick={handleToggle}
        className={classNames('dropdown-button', { active: open })}
      >
        <Icon id='save' icon={SaveIcon} />
        <span className='dropdown-button__label'>임시저장</span>
      </button>

      <Popover isOpen={open} onClose={handleClose} offset={5} reference={targetElement}>
        {({ props, placement }) => (
          <div {...props}>
            <div className={`dropdown-animation language-dropdown__dropdown ${placement}`} style={{ width: '280px' }}>
              <div className='language-dropdown__dropdown__results emoji-mart-scroll' role='listbox' style={{ maxHeight: '450px' }}>
                
                <div 
                  className='language-dropdown__dropdown__results__item' 
                  onClick={handleSaveCurrent}
                  style={{ 
                    fontWeight: 'bold', 
                    color: 'var(--color-text-on-brand-base)', 
                    borderColor: 'var(--color-bg-brand-base)',
                    background: 'var(--color-bg-brand-base)',
                    justifyContent: 'center', 
                    padding: '12px' 
                  }}
                >
                  <span>+ 현재 작성 중인 글 저장하기</span>
                </div>

                {initialDraft && (
                  <div 
                    className='language-dropdown__dropdown__results__item'
                    title={initialDraft} // 👈 호버 시 전체 내용 툴팁
                    onClick={() => {
                      if (window.confirm('자동 저장된 글을 불러오시겠습니까? 현재 작성 중인 내용이 덮어씌워집니다.')) {
                        onLoad(initialDraft);
                        handleClose();
                      }
                    }}
                    style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      padding: '12px 10px', 
                      backgroundColor: 'var(--color-bg-base)', 
                      borderBottom: '1px solid var(--color-border-primary)',
                      cursor: 'pointer',
                      alignItems: 'flex-start'
                    }}
                  >
                    <span style={{ fontSize: '11px', color: 'var(--color-text-light)', marginBottom: '4px' }}>자동 임시저장</span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px', width: '100%' }}>
                      {initialDraft}
                    </span>
                  </div>
                )}

                {drafts.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-light)', fontSize: '13px' }}>
                    저장된 글이 없습니다.
                  </div>
                ) : (
                  <>
                    {drafts.map((draft) => (
                      <div
                        key={draft.id}
                        className='language-dropdown__dropdown__results__item'
                        title={draft.text} // 👈 호버 시 전체 내용 툴팁
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          padding: '12px 10px', 
                          borderBottom: '1px solid var(--color-border-primary)' 
                        }}
                        onClick={() => {
                          if (window.confirm('이 글을 불러오시겠습니까? 현재 작성 중인 내용이 덮어씌워집니다.')) {
                            onLoad(draft.text);
                            handleClose();
                          }
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', marginRight: '10px', width: '100%' }}>
                          <span style={{ fontSize: '11px', color: 'var(--color-text-light)', marginBottom: '4px' }}>{draft.date}</span>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px' }}>
                            {draft.text}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => handleDelete(e, draft.id)}
                          style={{ background: 'transparent', border: 'none', color: 'var(--color-text-light)', cursor: 'pointer', padding: '5px' }}
                          title="삭제"
                        >
                          <Icon id='delete' icon={DeleteIcon} />
                        </button>
                      </div>
                    ))}
                    
                    <div 
                      className='language-dropdown__dropdown__results__item' 
                      onClick={() => {
                        if (window.confirm('저장된 모든 임시저장 글을 삭제하시겠습니까? (자동 임시저장 제외)')) {
                          saveDraftsToStorage([]);
                        }
                      }}
                      style={{ 
                        justifyContent: 'center', 
                        padding: '12px',
                        color: '#e53935', 
                        fontSize: '13px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      <span>모두 삭제하기</span>
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>
        )}
      </Popover>
    </>
  );
};